import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import PhoneNumber from 'components/field/PhoneNumber';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, SMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const SendTestSMSForm = NiceModal.create(({ smsSettingID }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					orderID: yup.string().nullable().required(),
					phone: yup.string().nullable().required().phoneNumber()
				})
				.required(),
		[]
	);

	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const modal = useModal();
	const [isLoading, setIsLoading] = useState(false);
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderID: null,
			phone: null,
			smsSettingID
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(SMS.SEND_TEST_SMS_ACTION, {
				smsSettingID,
				orderID: data.orderID,
				phone: data.phone
			});
			if (response.status === 200) {
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response.data?.code === 'error.SMS002') {
				showError({ message: 'errorMsg.smsTypeNotBeUpdatedForThisOrderStatus' });
			} else {
				AppLogger.error(error);
				showError();
			}
		}
		setIsLoading(false);
		modal.remove();
	};

	const orderIDAutocompleteColumns = useMemo(() => [{ accessor: 'orderID', width: 200 }], []);

	const defaultColSpan = {
		md: 6
	};
	return (
		<Modal maxWidth="medium" fullwidth className="modal-sendTestSMSForm">
			<ModalTitle>
				<Typography variant="p1">
					{t('sendTestSMSForm')} (<strong>{smsSettingID}</strong>)
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Alert content="sendTestSMSFormInfoText" className="mb-3" showIcon />

					<FormItem label="orderNo" colSpan={defaultColSpan} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ORDERS}
							columns={orderIDAutocompleteColumns}
							selectionLabelAccessor={['orderID']}
							name={'orderID'}
							value={watch('orderID')}
							setValue={(value) => {
								setValue('orderID', value?.ID ?? null);
							}}
						/>
					</FormItem>

					<FormItem label="mobileNumber" colSpan={defaultColSpan} required>
						<PhoneNumber
							name="phone"
							value={watch('phone')}
							onChange={(value) => {
								setValue('phone', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						if (isDirty && !isSubmitSuccessful && !isSubmitting) {
							NiceModal.show(ConfirmationModal, {
								yesAction: () => {
									modal.remove();
								}
							});
						} else {
							modal.remove();
						}
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faPaperPlaneTop}
					label="send"
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default SendTestSMSForm;
